<template>
    <div>
        <v-form ref="patient-form" v-model="formValid" :class="formStyleClasses" @input="handleFormInput">
            <v-row no-gutters>
                <v-col md="9" lg="10" sm="9" cols="8" >
                    <v-combobox
                            ref="name"
                            class="form-item required-field"
                            :counter="100"
                            :rules="nameRules"
                            :loading="searchInProgress"
                            v-model="editedPatient.name"
                            :items="matchedPatients"
                            :search-input.sync="search"
                            label="*Name"
                            item-text="name"
                            item-value="id"
                            :append-outer-icon="nameSelectorEditIcon"
                            required
                            hide-selected
                            persistent-hint
                            @click:append-outer="handleNameSelectorEdit"
                            @input="handleNameChange"
                            @update:search-input="handleChangePatientNameSearch"
                    >
                        <template v-slot:item="{ index, item }">
                            <v-list-item-content>
                                <v-list-item-title>{{item.name}}</v-list-item-title>
                                <v-list-item-subtitle>
                                    <span v-if="item.birthday"><v-icon color="gray" small>fas fa-birthday-cake</v-icon> {{item.birthday}}</span>
                                    <span v-if="item.email" class="ml-2"><v-icon color="gray" small>fas fa-at</v-icon> {{item.email}}</span>
                                    <span v-if="item.phone" class="ml-2"><v-icon color="gray"
                                                                                 small>fas fa-phone</v-icon> {{item.phone}}</span>
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </template>
                    </v-combobox>
                </v-col>
                <v-col md="3" lg="2" sm="3" cols="4">
                    <v-select
                            ref="gender"
                            class="form-item required-field"
                            :disabled="controlsBlocked"
                            v-model="editedPatient.gender"
                            :rules="genderRules"
                            :items="availableGenders"
                            item-text="title"
                            item-value="value"
                            required
                            label="*Gender"
                    ></v-select>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col cols="6">
                    <simple-birthday-selector
                            ref="birthdaySelector"
                            :disabled="controlsBlocked"
                            :no-data-enabled="!isNewPatient"
                            :birthday="editedPatient.birthday"
                            @input="handleBirthdayInput"
                            @validation-changed="handleBirthdayValidationChanged"></simple-birthday-selector>
                </v-col>
                <v-col cols="3">
                    <slot name="age-selector">
                    </slot>
                </v-col>
            </v-row>
<!--            <v-row no-gutters>-->
<!--                <v-col>-->
<!--                    <v-text-field-->
<!--                            ref="address"-->
<!--                            :disabled="controlsBlocked"-->
<!--                            class="form-item"-->
<!--                            v-model="address"-->
<!--                            label="Address"-->
<!--                            :rules="addressRules"-->
<!--                    ></v-text-field>-->
<!--                </v-col>-->
<!--            </v-row>-->
            <v-row no-gutters>
<!--                <v-col md="4" lg="4">-->
<!--                    <v-text-field-->
<!--                            :disabled="controlsBlocked"-->
<!--                            class="form-item"-->
<!--                            v-model="phone"-->
<!--                            label="Phone"-->
<!--                            :rules="phoneRules"-->
<!--                            placeholder="+7-111-111-11-11"-->
<!--                    ></v-text-field>-->
<!--                </v-col>-->
                <v-col cols="9">
                    <v-text-field
                            ref="email"
                            :disabled="controlsBlocked"
                            v-model="email"
                            class="form-item"
                            label="Email"
                            :rules="emailRules"
                    ></v-text-field>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col>
                    <v-textarea
                            ref="comment"
                            :disabled="controlsBlocked"
                            v-model="editedPatient.comment"
                            class="form-item"
                            label="Comment"
                            rows="3"
                            :rules="commentRules"
                    ></v-textarea>
                </v-col>
            </v-row>
        </v-form>
    </div>
</template>

<script>

    import api from '../../../store/modules/patients/api'
    import _ from 'lodash'
    import NotificationService from '../../../services/notificationService';
    import {validateStringLength} from '../../../helpers/validation'
    import {noDataValue} from '../../../dictionaries/nodata'
    import {mapActions, mapGetters, mapState} from 'vuex'
    import {getStringOrNull} from '../../../helpers/strings'
    import {reformatDate} from '../../../helpers/timeformats'
    import SimpleBirthdaySelector from '../../Common/SimpleBirthdaySelector'

    export default {
        name: "PatientForm",
        props: {
            patient: Object,
            caseId: String
        },
        components: {
            SimpleBirthdaySelector
        },
        data() {
            return {
                formValid: false,
                pickerDate: '1980-01',
                editedPatient: this.initEditedPatient(this.patient),
                searchInProgress: false,
                matchedPatients: [],
                search: null,
                birthdayMenu: false,
                nameRules: [
                    v => !!v || 'Enter patient name',
                    function (v) {
                        let value = v
                        if (_.isObject(v)) {
                            value = v.name
                        }

                        return (value && value.length <= 100) || 'The length of the name should be no more than 100 characters'
                    }
                ],
                genderRules: [
                    v => !!v || 'Select patient gender',
                ],
                birthdayRules: [
                    v => !!v || 'Select patient birthday',
                ],
                addressRules: [
                    _.partial(validateStringLength, _, 100, 'The length of the address should be no more than 100 characters')
                ],
                phoneRules: [
                    _.partial(validateStringLength, _, 25, 'The length of the phone number should be no more than 25 characters')
                ],
                emailRules: [
                    _.partial(validateStringLength, _, 50, 'The length of the email should be no more than 50 characters')
                ],
                commentRules: [
                    _.partial(validateStringLength, _, 1000, 'The length of the comment should be no more than 1000 characters')
                ],
                noDataOptions: [noDataValue]
            }
        },
        computed: {
            ...mapState({
                availableGenders: 'availableGenders'
            }),
            ...mapGetters({
                progress: 'progress'
            }),
            controlsBlocked() {
                return this.existingPatient
            },
            existingPatient() {
                return this.editedPatient.id !== null
            },
            nameSelectorEditIcon() {
                if (this.existingPatient) {
                    return 'fas fa-pencil-alt'
                }

                return null
            },
            formStyleClasses() {
                return {
                    'mb-3': false,
                }
            },
            isNewPatient() {
                return _.get(this.editedPatient, 'id', null) === null
            },
            phone: {
                get() {
                    return this.editedPatient.phone
                },
                set(value) {
                    this.editedPatient.phone = getStringOrNull(value)
                }
            },
            address: {
                get() {
                    return this.editedPatient.address
                },
                set(value) {
                    this.editedPatient.address = getStringOrNull(value)
                }
            },
            email: {
                get() {
                    return this.editedPatient.email
                },
                set(value) {
                    this.editedPatient.email = getStringOrNull(value)
                }
            }
        },
        watch: {
            birthdayMenu(val) {
                val && setTimeout(() => (this.$refs.picker.activePicker = 'YEAR'))
            },
            patient(newPatient) {
                this.editedPatient = this.initEditedPatient(newPatient)
            }
        },
        methods: {
            ...mapActions('patients', {
                updatePatient: 'updatePatient'
            }),
            ...mapActions('pgtCases', {
               getCasesByPatient: 'getCasesByPatient'
            }),
            handleChangePatientNameSearch(patientName) {
                if (!patientName || patientName.length === 0) {
                    return
                }

                this.searchPatientByName(patientName)
            },

            handleNameChange(value) {
                if (_.isObject(value)) {
                    this.editedPatient.id = value.id
                    this.editedPatient.name = value.name
                    this.editedPatient.address = value.address
                    this.editedPatient.email = value.email
                    this.editedPatient.phone = value.phone
                    this.editedPatient.gender = value.gender
                    this.editedPatient.birthday = value.birthday
                    this.editedPatient.comment = value.comment
                }
            },
            handleNameSelectorEdit() {
                this.$router.push(`/patients/${this.editedPatient.id}/edit`)
            },
            searchPatientByName: _.debounce(async function (name) {
                if (this.searchInProgress) {
                    return
                }

                this.searchInProgress = true

                try {
                    let response = await api.getByName(name, 20)

                    this.matchedPatients = response.items
                } catch (e) {
                    NotificationService.error('Unable to find patients by name. Try again later')
                }

                this.searchInProgress = false

            }, 500),
            getFormData() {
                return this.editedPatient
            },
            validate() {
                return this.$refs['patient-form'].validate() && this.$refs['birthdaySelector'].validate()
            },
            isValid() {
                let subFormsValid = true

                if (this.$refs['birthdaySelector']) {
                    subFormsValid = this.$refs['birthdaySelector'].isValid()
                }

                return this.formValid && subFormsValid
            },
            handleFormInput(value) {
                this.$emit('validation-changed', this.isValid())
            },
            handleBirthdayValidationChanged(value) {
                this.$emit('validation-changed', this.isValid())
            },
            getValueOrNoData(value) {
                if (!this.isNewPatient && value === null) {
                    return noDataValue
                }

                return value
            },
            reset() {
                this.editedPatient = {
                    id: null,
                    name: null,
                    address: null,
                    birthday: null,
                    gender: null,
                    phone: null,
                    email: null,
                    comment: null
                }
            },
            handleBirthdayInput(value) {
                this.editedPatient.birthday = value
                this.$emit('patient-birthday-changed', value)
            },
            initEditedPatient(patient) {
                return {
                    id: _.cloneDeep(_.get(patient, 'id', null)),
                    name: _.cloneDeep(_.get(patient, 'name', null)),
                    address: _.cloneDeep(_.get(patient, 'address', null)),
                    birthday: _.cloneDeep(_.get(patient, 'birthday', null)),
                    gender: _.cloneDeep(_.get(patient, 'gender', null)),
                    phone: _.cloneDeep(_.get(patient, 'phone', null)),
                    email: _.cloneDeep(_.get(patient, 'email', null)),
                    comment: _.cloneDeep(_.get(patient, 'comment', null))
                }
            },
            reformatDate: reformatDate
        }
    }
</script>

<style scoped>
    .form-item {
        margin: 10px !important;
    }

    .patient-edit-label {
        font-size: 11px;
        color: #1976d2;
    }

    .form-edit-mode {
        border: 1px dashed;
        color: #1976d23b;
    }
</style>